import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Typography, Box, Grid, Skeleton } from '@mui/material';
import moment from 'moment';

import { FHSelect, FHInputField } from '../../../../../base';
import { useTreatmentPlanEditable } from '../../../hooks/useTreatmentPlanEditable';
import { treatmentRecommendationActions } from '../../../../../../store';
import { validateSchema } from '../../../../../../helpers/validation-helpers';
import { RecommendationServiceSchema, MandatoryServiceSchema } from '../../utils/validationSchema';
import { getActivePrimaryPolicy, getServiceName } from '../../../utils';
import { Themes } from '../../../../../../constants';
import { ServiceCodeTypes } from '../../../../billings/claim-info/enum';
import { ERROR_STATE, FREQUENCY } from '../../constants/general';
import { getRecommendationServices } from '../../utils/general';
import { BAYS } from '../../../../../../constants';

import type { RootState } from '../../../../../../store';
import type {
  IRecommendationService,
  IServiceCodeDescription,
  IServicesTransformed,
  IUnsavedRecommendationService,
  SelectValueType,
} from '../../../../../../utils/types';

interface RecommendationServicesProps {
  values: IServicesTransformed;
  onUpdate: (service: IRecommendationService) => void;
}

const ServiceTableHeader = () => (
  <Box textAlign="left">
    <Grid container spacing={2} mb={1}>
      <Grid item xs={4.5}>
        <Typography fontWeight="bold">Service</Typography>
      </Grid>
      <Grid item xs={2.5}>
        <Typography fontWeight="bold">Hours</Typography>
      </Grid>
      <Grid item xs={2.5}>
        <Typography fontWeight="bold">Units</Typography>
      </Grid>
      <Grid item xs={2.5}>
        <Typography fontWeight="bold">Per</Typography>
      </Grid>
    </Grid>
  </Box>
);

function RecommendationServices(props: RecommendationServicesProps) {
  const { values, onUpdate } = props;
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEditable = useTreatmentPlanEditable();
  const { selectedClientPlans, loading: clientPolicyLoading } = useSelector(
    (state: RootState) => state.clients,
  );
  const { current: treatmentPlan, loading: treatmentPlanLoading } = useSelector(
    (state: RootState) => state.treatmentPlans,
  );
  const { recommendation, loading: recommendationLoading } = useSelector(
    (state: RootState) => state.treatmentRecommendations,
  );

  const [services, setServices] = useState<IServicesTransformed>(values || {});
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [serviceCodeLabels, setServiceCodeLabels] = useState<IServiceCodeDescription[]>([]);
  const [preferredServiceCodeType, setPreferredServiceCodeType] = useState<ServiceCodeTypes>(
    ServiceCodeTypes.Cpt,
  );

  const tpBay = treatmentPlan?.bay ?? BAYS.BAY_2_6;

  useEffect(() => {
    setServices(values);
  }, [values]);

  useEffect(() => {
    if (!selectedClientPlans || !treatmentPlan?.id) return;

    const currentDate = moment();
    const recommendationDate = recommendation?.date_start
      ? moment(recommendation.date_start)
      : null;

    const primaryPolicy = getActivePrimaryPolicy(
      selectedClientPlans,
      recommendationDate,
      currentDate,
    );

    const preferredServiceCodeType = Object.values(ServiceCodeTypes).find(
      (serviceCodeType) => serviceCodeType === primaryPolicy?.payor.preferred_service_code_type,
    );
    setPreferredServiceCodeType(preferredServiceCodeType ?? ServiceCodeTypes.Cpt);
    setServiceCodeLabels(primaryPolicy?.payor?.service_code_descriptions ?? []);
  }, [selectedClientPlans, treatmentPlan?.id, recommendation?.date_start]);

  useEffect(() => {
    if (!services || !treatmentPlan?.id || !recommendation?.id) return;
    const unsavedServices: IUnsavedRecommendationService[] = [];
    Object.values(services).forEach((service: IRecommendationService) => {
      if (!service.id) {
        const data = { ...service, tpId: treatmentPlan.id };
        unsavedServices.push(data);
      }
    });

    unsavedServices.forEach((service: IUnsavedRecommendationService) => {
      if (!service.id && service.tpId && recommendation?.id) {
        const { tpId, ...data } = service;
        dispatch(
          treatmentRecommendationActions.createService({
            treatmentPlanId: tpId,
            treatmentRecommendationId: recommendation?.id,
            data: data,
            getAccessTokenSilently,
          }),
        );
      }
    });
    dispatch(treatmentRecommendationActions.updateState({ key: 'unsavedServices', data: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendation?.id]);

  useEffect(() => {}, [services]);

  useEffect(() => {
    if (!errors) return;
    updateErrorCount(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  useEffect(() => {
    if (!services) return;

    const err: { [key: string]: boolean } = {};

    getRecommendationServices(tpBay).forEach((service) => {
      if (service.required && !services[service.code]?.hours) {
        err[`hours-${service.code}`] = true;
      } else if (
        (service.required && services[service.code]?.hours && errors[`hours-${service.code}`]) ||
        !service.required
      ) {
        delete err[`hours-${service.code}`];
      }
    });
    setErrors(err);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, tpBay]);

  const updateErrorCount = (errors: Record<string, boolean>) => {
    const errorCount = Object.values(errors).filter((value) => value).length;

    dispatch(
      treatmentRecommendationActions.updateErrorState({
        [ERROR_STATE.SERVICES]: errorCount,
      }),
    );
  };

  const handleValidate = (
    serviceCode: string,
    key: string,
    data: IRecommendationService = services[serviceCode],
    isServiceRequired = false,
  ) => {
    if (!data) return;
    const schema = isServiceRequired ? MandatoryServiceSchema : RecommendationServiceSchema;
    const isValid = validateSchema(schema, key, data);
    setErrors((prev: Record<string, boolean>) => ({
      ...prev,
      [`${key}-${serviceCode}`]: !isValid,
    }));
  };

  const handleUpdateServicesField = (
    serviceCode: string,
    key: string,
    value: string | number,
    isServiceRequired = false,
  ) => {
    const defaults = {
      frequency: 'weekly',
    };

    const keyValuePairs =
      key === 'hours'
        ? { [key]: Number(value), units: Number(value) * 4 }
        : key === 'units'
          ? { [key]: Number(value), hours: Number(value) / 4 }
          : { [key]: String(value) };

    const updatedService = {
      ...defaults,
      ...services[serviceCode],
      service_code: serviceCode,
      ...keyValuePairs,
    };

    setServices((prev: IServicesTransformed) => ({
      ...prev,
      [serviceCode]: updatedService,
    }));

    handleValidate(serviceCode, key, updatedService, isServiceRequired);

    if (key === 'frequency') {
      onUpdate({ ...services[serviceCode], [key]: String(value) });
    }
  };

  return (
    <Box>
      <ServiceTableHeader />
      <Grid container mt={3}>
        {getRecommendationServices(tpBay).map((service) => {
          return (
            <Grid container spacing={2} mb={2} key={service.code}>
              <Grid item xs={4.5} display={'flex'} alignItems={'center'}>
                {clientPolicyLoading || recommendationLoading || treatmentPlanLoading ? (
                  <Skeleton variant="text" width={250} height={40} />
                ) : (
                  <Typography textAlign={'left'} sx={{ lineHeight: 1 }}>
                    {getServiceName(service, preferredServiceCodeType, serviceCodeLabels)}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={2.5} sx={{ position: 'relative' }}>
                <FHInputField
                  data-testid={`${service.code}_textField`}
                  sx={{ minWidth: 50 }}
                  type="number"
                  inputHeight={40}
                  fullWidth
                  colorTheme={Themes.Primary}
                  disabled={!isEditable}
                  value={services?.[service.code]?.hours || ''}
                  onChange={(e) => {
                    handleUpdateServicesField(
                      service.code,
                      'hours',
                      +e.target.value,
                      Boolean(service.required),
                    );
                  }}
                  onFocus={() =>
                    handleValidate(
                      service.code,
                      'hours',
                      services[service.code],
                      Boolean(service.required),
                    )
                  }
                  onBlur={() => onUpdate(services?.[service.code])}
                  error={errors[`hours-${service.code}`]}
                />
              </Grid>
              <Grid item xs={2.5}>
                <FHInputField
                  data-testid={`${service.code}_unitsTextField`}
                  sx={{ minWidth: 50 }}
                  type="number"
                  inputHeight={40}
                  fullWidth
                  colorTheme={Themes.Primary}
                  disabled
                  value={services?.[service.code]?.hours * 4 || ''}
                />
              </Grid>
              <Grid item xs={2.5}>
                <FHSelect
                  data-testid="frequencyDropDown"
                  sx={{ minWidth: '150px' }}
                  inputHeight={40}
                  fullWidth
                  colorTheme={Themes.Primary}
                  disabled={!isEditable}
                  options={FREQUENCY.map((frequency) => ({
                    value: frequency,
                    label: frequency,
                  }))}
                  value={services?.[service.code]?.frequency || service.frequency}
                  onUpdate={(value: SelectValueType) => {
                    handleUpdateServicesField(service.code, 'frequency', value);
                  }}
                  onFocus={() => handleValidate(service.code, 'frequency')}
                  error={errors[`frequency-${service.code}`]}
                />
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export default RecommendationServices;
